import React, { useState, useRef, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useDebounce } from "../../hooks/useDebounce";
import {
  CollectionType,
  searchCollections,
} from "../../apis/bacchus/collections";

export interface CollectionSelectOption {
  label: string;
  value: number | string;
  ids: string[] | number[];
}

interface Props {
  placeholder?: string;
  disabled?: boolean;
  type: CollectionType;
  onSelect?: (option: CollectionSelectOption) => void;
  excludeId?: string;
}

export const CollectionSelect: React.FC<Props> = ({
  placeholder = "Search",
  disabled = false,
  type,
  onSelect,
  excludeId,
}) => {
  const [value, setValue] = useState<CollectionSelectOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<CollectionSelectOption[]>([]);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debouncedInputValue = useDebounce(inputValue, 500);

  const loadOptions = async () => {
    try {
      const { data } = await searchCollections(type, {
        search: debouncedInputValue,
        page: 1,
        pageSize: 1000,
      });

      if (data) {
        const filteredData = excludeId
          ? data.filter((collection) => collection.id !== excludeId)
          : data;

        setOptions(
          filteredData.map((collection) => ({
            label: collection.name,
            value: collection.id,
            ids: collection.blob.ids,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to load collections:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadOptions();
  }, [type, debouncedInputValue, excludeId]);

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: CollectionSelectOption | null
  ) => {
    if (newValue) {
      onSelect?.(newValue);
      setValue(null);
      setInputValue("");
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  return (
    <Autocomplete
      value={value}
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loading={loading}
      disabled={disabled}
      size="small"
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            inputRef={inputRef}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
    />
  );
};

export default CollectionSelect;
